import { useEffect, useRef, useState } from 'react';
import { X } from 'shared/icons';
import { useTokens } from './tokens';

/**
 * Popover de anúncios linkados ao SKU — clique no badge "+N anúncios" abre
 * com a lista MLBs + título atual + link pro ML. Em MLB mode (1 row = 1
 * anúncio) o popover não aparece (a coluna mostra o título direto inline).
 *
 * Props:
 *   listings: Array<{mlb, title, permalink}>
 *   anchor:   string mostrado no botão antes do click ("+2 anúncios")
 *   buttonStyle: estilo do botão trigger (opcional)
 */
export function MlbListingsPopover({ listings, anchor, buttonStyle = {} }) {
    const T = useTokens();
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const onClickOutside = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        const onKey = (e) => e.key === 'Escape' && setOpen(false);
        document.addEventListener('mousedown', onClickOutside);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onClickOutside);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    if (!Array.isArray(listings) || listings.length === 0) return null;

    return (
        <span ref={wrapRef} style={{ position: 'relative', display: 'inline-block' }}>
            <button
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    setOpen((v) => !v);
                }}
                style={{
                    background: 'transparent',
                    border: 'none',
                    padding: '2px 6px',
                    cursor: 'pointer',
                    fontSize: 11,
                    color: T.accent,
                    fontFamily: T.mono,
                    borderRadius: 3,
                    ...buttonStyle,
                }}
                title="Ver anúncios vinculados"
            >
                {anchor}
            </button>
            {open && (
                <div
                    style={{
                        position: 'absolute',
                        top: 'calc(100% + 4px)',
                        left: 0,
                        zIndex: 200,
                        background: T.bg,
                        border: `1px solid ${T.lineMid}`,
                        borderRadius: 6,
                        boxShadow: '0 12px 32px rgba(0,0,0,0.2)',
                        minWidth: 320,
                        maxWidth: 480,
                        maxHeight: 360,
                        overflow: 'auto',
                        padding: 8,
                    }}
                    data-faid="Mlb_Listings_Popover_Operacoes"
                >
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '4px 6px 8px',
                            borderBottom: `1px solid ${T.line}`,
                            marginBottom: 6,
                        }}
                    >
                        <span
                            style={{
                                flex: 1,
                                fontSize: 11,
                                fontWeight: 600,
                                color: T.muted,
                                textTransform: 'uppercase',
                                letterSpacing: 0.5,
                            }}
                        >
                            {listings.length} anúncio{listings.length !== 1 ? 's' : ''}
                        </span>
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                setOpen(false);
                            }}
                            aria-label="Fechar"
                            style={{
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                color: T.muted,
                                padding: 2,
                                display: 'inline-flex',
                            }}
                        >
                            <X size={12} />
                        </button>
                    </div>
                    {listings.map((l) => (
                        <div
                            key={l.mlb}
                            style={{
                                padding: '6px 6px',
                                fontSize: 12,
                                borderBottom: `1px dashed ${T.line}`,
                            }}
                        >
                            <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                                <a
                                    href={
                                        l.permalink ||
                                        `https://www.mercadolivre.com.br/anuncios?search=${encodeURIComponent(l.mlb)}`
                                    }
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    style={{
                                        fontFamily: T.mono,
                                        fontSize: 11,
                                        color: T.accent,
                                        textDecoration: 'none',
                                        whiteSpace: 'nowrap',
                                    }}
                                    title="Abrir no Mercado Livre (Ctrl+click pra nova aba; MOUSE3 também funciona)"
                                >
                                    {l.mlb} ↗
                                </a>
                                <span
                                    style={{
                                        color: T.ink,
                                        fontSize: 12,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        textTransform: 'uppercase',
                                    }}
                                    title={l.title || ''}
                                >
                                    {l.title || '— sem título —'}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </span>
    );
}
